import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  Typography,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Fade,
  Chip,
  Pagination,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import {
  Add,
  Edit,
  Delete,
  Refresh,
  Groups,
  Search,
  LocationOn,
  Phone,
  Email,
  Language,
  Assignment,
  ArrowBack,
  Visibility,
  CheckCircle,
  LibraryBooks,
} from "@mui/icons-material";
import {
  fetchChitalishteById,
  createChitalishte,
  updateChitalishte,
  deleteChitalishte,
  fetchInformationCardsByChitalishte,
  createInformationCard,
  updateInformationCard,
  deleteInformationCard,
  clearError,
  selectAllChitalishta,
  selectChitalishtaLoading,
  selectChitalishtaError,
  selectInformationCardsByChitalishteId,
  selectInformationCardsLoading,
  selectInformationCardsError,
} from "../../../store/slices/chitalishteAdminSlice";
import { Tabs, Tab } from "@mui/material";
import ChitalishteAuditHistory from "../chitalishte/ChitalishteAuditHistory";
import { History } from "@mui/icons-material";

const statusToEnum = {
  "Действащо": "ACTIVE",
  "Неактивно": "INACTIVE",
  "Закрито": "CLOSED",
};

const ChitalishteForm = ({
  open,
  onClose,
  chitalishte,
  onSubmit,
  feedbackId,
  chitalishteId,
  onEditLibrary,
}) => {
  const dispatch = useDispatch();
  const allChitalishta = useSelector(selectAllChitalishta);
  const chitalishtaLoading = useSelector(selectChitalishtaLoading);

  const [formData, setFormData] = useState({
    name: "",
    town: "",
    municipality: "",
    region: "",
    address: "",
    phone: "",
    email: "",
    chitalishtaUrl: "",
    chairman: "",
    secretary: "",
    bulstat: "",
    status: "ACTIVE",
    registrationNumber: "",
    feedbackId: "",
  });

  const [errors, setErrors] = useState({});
  const [autoUpdateFeedback, setAutoUpdateFeedback] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const chitalishteToEdit =
    chitalishte ||
    (chitalishteId
      ? allChitalishta.find((c) => c.id == chitalishteId)
      : null);
  
  const isLoading = (open && chitalishteId && !chitalishteToEdit) || chitalishtaLoading;

  useEffect(() => {
    if (open) {
      if (chitalishteId && !chitalishteToEdit) {
        dispatch(fetchChitalishteById(chitalishteId));
      }

      if (chitalishteToEdit) {
        const currentStatus = chitalishteToEdit.status;
        setFormData({
          name: chitalishteToEdit.name || "",
          town: chitalishteToEdit.town || "",
          municipality: chitalishteToEdit.municipality || "",
          region: chitalishteToEdit.region || "",
          address: chitalishteToEdit.address || "",
          phone: chitalishteToEdit.phone || "",
          email: chitalishteToEdit.email || "",
          chitalishtaUrl: chitalishteToEdit.chitalishtaUrl || "",
          chairman: chitalishteToEdit.chairman || "",
          secretary: chitalishteToEdit.secretary || "",
          bulstat: chitalishteToEdit.bulstat || "",
          status: statusToEnum[currentStatus] || currentStatus || "",
          registrationNumber: chitalishteToEdit.registrationNumber || "",
          feedbackId: feedbackId || "",
        });
      } else {
        // Reset form if no chitalishte is being edited, but keep feedbackId if present
        setFormData({
          name: "",
          town: "",
          municipality: "",
          region: "",
          address: "",
          phone: "",
          email: "",
          chitalishtaUrl: "",
          chairman: "",
          secretary: "",
          bulstat: "",
          status: "ACTIVE",
          registrationNumber: "",
          feedbackId: feedbackId || "",
        });
      }
      setAutoUpdateFeedback(!!feedbackId);
      setErrors({});
    }
  }, [open, chitalishteToEdit, feedbackId, chitalishteId, dispatch]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = "Името е задължително";
    if (!formData.town.trim())
      newErrors.town = "Населеното място е задължително";
    if (!formData.municipality.trim())
      newErrors.municipality = "Общината е задължителна";
    if (!formData.region.trim()) newErrors.region = "Областта е задължителна";

    if (formData.feedbackId && formData.feedbackId.trim()) {
      const uuidRegex =
        /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(formData.feedbackId.trim())) {
        newErrors.feedbackId = "Невалиден формат за ID на обратна връзка";
      }
    }

    if (
      formData.chitalishtaUrl &&
      !/^https?:\/\/.+/.test(formData.chitalishtaUrl)
    ) {
      newErrors.chitalishtaUrl = "Невалиден URL адрес";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      setConfirmOpen(true);
    }
  };

  const handleConfirm = () => {
    const submitData = {
      ...formData,
      status: formData.status === "" ? null : formData.status,
      shouldUpdateFeedback:
        autoUpdateFeedback &&
        formData.feedbackId &&
        formData.feedbackId.trim(),
    };
    onSubmit(submitData);
    setConfirmOpen(false);
  };

  const handleChange = (field) => (event) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleAutoUpdateChange = (event) => {
    setAutoUpdateFeedback(event.target.checked);
  };

    const canShowAutoUpdateCheckbox = () => {
      return (
        formData.feedbackId && formData.feedbackId.trim() !== "" && !errors.feedbackId
      );
    };
  
    const getConfirmationMessage = () => {
      if (canShowAutoUpdateCheckbox()) {
        if (autoUpdateFeedback) {
          return "Сигурни ли сте, че искате да запазите промените? Статусът на свързаната обратна връзка ще бъде променен на 'Обработен'.";
        } else {
          return "Сигурни ли сте, че искате да запазите промените? Статусът на свързаната обратна връзка НЯМА да бъде променен.";
        }
      }
      return "Сигурни ли сте, че искате да запазите промените по читалището?";
    };
  
    return (
      <>
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
          <DialogTitle>
            {chitalishte || chitalishteId
              ? "Редактиране на читалище"
              : "Добавяне на ново читалище"}
          </DialogTitle>
          <DialogContent>
            {isLoading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 8,
                }}
              >
                <CircularProgress />
                <Typography sx={{ ml: 2 }}>Зареждане на данни...</Typography>
              </Box>
            ) : (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Име на читалището *"
                    value={formData.name}
                    onChange={handleChange("name")}
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Населено място *"
                    value={formData.town}
                    onChange={handleChange("town")}
                    error={!!errors.town}
                    helperText={errors.town}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Община *"
                    value={formData.municipality}
                    onChange={handleChange("municipality")}
                    error={!!errors.municipality}
                    helperText={errors.municipality}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Област *"
                    value={formData.region}
                    onChange={handleChange("region")}
                    error={!!errors.region}
                    helperText={errors.region}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Адрес"
                    value={formData.address}
                    onChange={handleChange("address")}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Телефон"
                    value={formData.phone}
                    onChange={handleChange("phone")}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Имейл"
                    value={formData.email}
                    onChange={handleChange("email")}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Уебсайт"
                    value={formData.chitalishtaUrl}
                    onChange={handleChange("chitalishtaUrl")}
                    error={!!errors.chitalishtaUrl}
                    helperText={errors.chitalishtaUrl}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Булстат"
                    value={formData.bulstat}
                    onChange={handleChange("bulstat")}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Председател"
                    value={formData.chairman}
                    onChange={handleChange("chairman")}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Секретар"
                    value={formData.secretary}
                    onChange={handleChange("secretary")}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Регистрационен номер"
                    value={formData.registrationNumber}
                    onChange={handleChange("registrationNumber")}
                  />
                </Grid>
  
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Статус</InputLabel>
                    <Select
                      value={formData.status}
                      onChange={handleChange("status")}
                      label="Статус"
                    >
                      <MenuItem value=""><em>(не е избран)</em></MenuItem>
                      <MenuItem value="ACTIVE">Действащо</MenuItem>
                      <MenuItem value="INACTIVE">Неактивно</MenuItem>
                      <MenuItem value="CLOSED">Закрито</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
  
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Feedback ID (опционално)"
                    value={formData.feedbackId}
                    onChange={handleChange("feedbackId")}
                    error={!!errors.feedbackId}
                    helperText={
                      errors.feedbackId ||
                      "Свържи тази промяна с конкретен feedback (UUID формат)"
                    }
                    placeholder="напр. 123e4567-e89b-12d3-a456-426614174000"
                  />
                </Grid>
  
                {canShowAutoUpdateCheckbox() && (
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={autoUpdateFeedback}
                          onChange={handleAutoUpdateChange}
                          color="primary"
                        />
                      }
                      label="Автоматично обнови статуса на обратната връзка на 'Обработен'"
                    />
                  </Grid>
                )}
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            { (chitalishteToEdit) && onEditLibrary && (
                <Button 
                    onClick={() => onEditLibrary(chitalishteToEdit, formData.feedbackId)} 
                    startIcon={<LibraryBooks />}
                    sx={{ marginRight: 'auto' }}
                >
                    Редактирай библиотека
                </Button>
            )}
            <Button onClick={onClose}>Отказ</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              disabled={isLoading}
              startIcon={(chitalishte || chitalishteId) ? <Edit /> : <Add />}
            >
              {(chitalishte || chitalishteId) ? "Запази" : "Добави"}
            </Button>
          </DialogActions>
        </Dialog>
        <Dialog
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>Потвърждение</DialogTitle>
          <DialogContent>
            <Typography>{getConfirmationMessage()}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Отказ</Button>
            <Button onClick={handleConfirm} variant="contained" color="primary">
              Потвърди
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };
export default ChitalishteForm;